import { lazy, Suspense, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Select, SearchInput } from "@/components/inputs";
import { PageHead } from "@/components/PageHead";
import { useQuery } from "@tanstack/react-query";

import { assetData } from "@/api/client";
import { ContextStrip } from "@/modules/asset_data/ContextStrip";
import { DevicePicker } from "@/modules/asset_data/picker";
import { RecordTable } from "@/modules/asset_data/RecordTable";

// lazy: Leaflet (~145KB) loads only when a History replay actually renders —
// every other page keeps the pre-map bundle size
const RouteReplay = lazy(() => import("@/modules/asset_data/RouteReplay").then((m) => ({ default: m.RouteReplay })));

/** value for <input type="datetime-local"> from a Date (local, minute precision). */
function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** THE DEFAULT WINDOW IS THE LAST 24 HOURS, everywhere on this page.
 *
 *  Dattatray: *"if use wants he can fetch the histry data with different time
 *  but the default should be be last 24 hours!"* — so this is a DEFAULT and
 *  not a fixed window: From, To and Resolution stay editable and Load re-runs
 *  whatever is in them. Arriving from the fleet map it also loads ITSELF,
 *  because nobody clicks History on a dot in order to then pick a date.
 *
 *  The policy lives HERE rather than in the link the map builds, so there is
 *  one place that decides what "history for this asset" means. */
const DEEP_HOURS = 24;
/** 24 h of 1 Hz data is ~86K records and the cap is 5,000, so a deep link MUST
 *  arrive bucketed or it would silently show the first few minutes and a
 *  truncation warning. 1-minute averages make it 1,440 rows. */
const DEEP_BUCKET_S = 60;

export function AssetDataHistoryPage() {
  const [imei, setImei] = useState("");
  const now = new Date();
  const [from, setFrom] = useState(
    localInput(new Date(now.getTime() - DEEP_HOURS * 3600_000)));
  const [to, setTo] = useState(localInput(now));
  // server-side downsampling: raw for short replays, bucketed for long windows
  // (the browser gets one averaged row per bucket, never the raw firehose).
  //
  // THE DEFAULT IS BUCKETED because the default window is now a day: 24 h of
  // 1 Hz data is ~86,000 records against a 5,000 cap, so "Raw records" over
  // the default window would show the first few minutes and a truncation
  // warning. Narrow the window and raw becomes the right choice again.
  const [bucket, setBucket] = useState(DEEP_BUCKET_S);
  const [range, setRange] = useState<{ imei: string; from: string; to: string; bucket: number } | null>(null);

  // `?imei=` is how the fleet map hands a device over (it opens this page in a
  // new tab). The controls are set as well as the query, so the From/To/
  // Resolution boxes show the window that was actually loaded — a control
  // displaying a value its state does not hold is one of the bugs D-84 lists.
  const [params, setParams] = useSearchParams();
  const deep = params.get("imei");
  useEffect(() => {
    if (!deep) return;
    const t1 = new Date();
    const f = localInput(new Date(t1.getTime() - DEEP_HOURS * 3600_000));
    const t = localInput(t1);
    setImei(deep);
    setFrom(f);
    setTo(t);
    setBucket(DEEP_BUCKET_S);
    setRange({ imei: deep, from: f, to: t, bucket: DEEP_BUCKET_S });
    // consume the param, so a later pick from the picker is not fought by a
    // stale URL on the next render
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete("imei"); return p; },
              { replace: true });
  }, [deep]);

  const q = useQuery({
    queryKey: ["live", "history", range?.imei, range?.from, range?.to, range?.bucket],
    queryFn: () => assetData.history(range!.imei, {
      from: new Date(range!.from).toISOString(),
      to: new Date(range!.to).toISOString(),
      bucket: range!.bucket ? String(range!.bucket) : undefined,
      limit: "5000",
    }),
    enabled: !!range,
  });
  const records = q.data?.records ?? [];
  const signals = q.data?.signals ?? [];

  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Data · History" title="Replay history"
        desc={<>Step back through a device's decoded records over any window (from the tel/his telemetry history) - same columns as the live table.</>} />

      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <DevicePicker imei={imei} onPick={setImei} />
        {imei && <div style={{ marginTop: 8 }}><ContextStrip imei={imei} /></div>}
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
          <div>
            <label className="simlab">From</label>
            <SearchInput type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="simlab">To</label>
            <SearchInput type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <label className="simlab">Resolution</label>
            <Select style={{ minWidth: 130 }} value={bucket}
              onChange={(e) => setBucket(Number(e.target.value))}>
              <option value={0}>Raw records</option>
              <option value={60}>1 min avg</option>
              <option value={600}>10 min avg</option>
              <option value={3600}>1 hour avg</option>
            </Select>
          </div>
          <button className="btn teal" style={{ marginBottom: 1 }} disabled={!imei}
            onClick={() => setRange({ imei, from, to, bucket })}
            title={!imei ? "pick a device first" : ""}>Load</button>
          {range && (
            <span style={{ fontSize: 11.5, color: "var(--t-mute)", paddingBottom: 6 }}>
              {q.isFetching ? "loading…" : `${records.length} record${records.length === 1 ? "" : "s"}`}
            </span>
          )}
        </div>
      </div>

      {range ? (
        <>
          {q.data?.truncated && (
            <div className="panel panel-pad" style={{ marginBottom: 14, borderLeft: "3px solid var(--warn)", color: "var(--t-mute)", fontSize: 12.5 }}>
              ⚠ This window holds more than 5,000 {range.bucket ? "buckets" : "records"} - showing the first 5,000.
              Narrow the time window{range.bucket ? "" : " or pick a coarser resolution"} to see everything.
            </div>
          )}
          <Suspense fallback={<div className="panel panel-pad" style={{ height: 120, color: "var(--t-dim)", fontSize: 12, marginBottom: 14 }}>loading map…</div>}>
            <RouteReplay records={records} />
          </Suspense>
          {/* newest-first for reading; the replay above keeps its own
              oldest→newest order for the scrubber */}
          <RecordTable records={records} signals={signals} maxHeight={520} newestFirst />
        </>
      ) : (
        <div className="panel panel-pad cap">
          Pick a device, then press Load - the window defaults to the last
          {" "}{DEEP_HOURS} hours, and you can change it to anything.
          <div style={{ marginTop: 6, fontSize: 11.5 }}>
            Or click an asset on the Overview map: History opens in a new tab
            with those {DEEP_HOURS} hours already loaded.
          </div>
        </div>
      )}
    </div>
  );
}
